import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { BaixaResponseDTO, BaixaDTO, TotalBaixasMoedaDTO } from '../../models/gerenciar-contas/baixa.model';
import { ExtratoDeCaixaPdfParams } from '../../models/movimento/extrato-de-caixa-pdf.model';

@Injectable({
  providedIn: 'root',
})
export class BaixaService {

  private baixaCriadaSubject = new BehaviorSubject<BaixaDTO | null>(null);
  public baixaCriada$ = this.baixaCriadaSubject.asObservable();

  constructor(private apiService: ApiService) { }

  get(url: string, data?: any): Observable<BaixaResponseDTO> {
    const queryParams = this.apiService.buildQueryParams(data);
    const path = queryParams ? `${url}?${queryParams}` : url;
    return this.apiService.read<BaixaResponseDTO>(path);
  }

  getSaldoInicial(conta: number | null, dataInicio: string): Observable<number> {
    const params: any = { dataInicio };
    if (conta != null) params.conta = conta;

    const queryParams = this.apiService.buildQueryParams(params);
    const path = queryParams ? `/baixa/saldo-inicial?${queryParams}` : '/baixa/saldo-inicial';

    return this.apiService.read<number>(path);
  }


  getById<T>(url: string): Observable<BaixaDTO> {
    return this.apiService.read<BaixaDTO>(url);
  }

  post<T>(url: string, data: BaixaDTO): Observable<BaixaDTO> {
    return this.apiService.create<BaixaDTO>(url, data).pipe(
      tap((baixaCriada: BaixaDTO) => {
        this.baixaCriadaSubject.next(baixaCriada);
      })
    );
  }

  put<T>(url: string, data: BaixaDTO): Observable<BaixaDTO> {
    return this.apiService.update<BaixaDTO>(url, data);
  }

  delete<T>(url: string): Observable<any> {
    return this.apiService.delete(url);
  }

  buscarComSaldoAnterior(
    conta?: number | null,
    dataInicio?: string | null,
    dataFim?: string | null,
    buscaGeral?: string | null,
    tipoDeTransacao?: string[] | null,
    page: number = 0,
    size: number = 10
  ): Observable<BaixaResponseDTO> {
    const params: any = { page, size };
    if (conta != null) params.conta = conta;
    if (dataInicio) params.dataInicio = dataInicio;
    if (dataFim) params.dataFim = dataFim;
    if (buscaGeral) params.buscaGeral = buscaGeral;
    if (tipoDeTransacao && tipoDeTransacao.length > 0) {
      params.tipoDeTransacao = tipoDeTransacao.join(',');
    }

    return this.apiService.readWithParams<BaixaResponseDTO>('/baixa/buscarComSaldoAnterior', params);
  }

  buscarPorFiltros(
    conta?: number | null,
    dataInicio?: string | null,
    dataFim?: string | null,
    buscaGeral?: string | null,
    tipoDeTransacao?: string[] | null,
    tipoDespesa?: string | null,
    tipoReceita?: string | null,
    pessoaId?: number | null,
    page: number = 0,
    size: number = 10,
    sort?: string | null
  ): Observable<BaixaResponseDTO> {
    const params: any = { page, size };
    if (conta != null) params.conta = conta;
    if (dataInicio) params.dataInicio = dataInicio;
    if (dataFim) params.dataFim = dataFim;
    if (buscaGeral) params.buscaGeral = buscaGeral;
    if (tipoDeTransacao && tipoDeTransacao.length > 0) {
      params.tipoDeTransacao = tipoDeTransacao.join(',');
    }
    if (tipoDespesa) params.tipoDespesa = tipoDespesa;
    if (tipoReceita) params.tipoReceita = tipoReceita;
    if (pessoaId != null) params.pessoaId = pessoaId;
    if (sort) params.sort = sort;

    return this.apiService.readWithParams<BaixaResponseDTO>('/baixa/buscarPorFiltros', params);
  }

  totalBaixas(
    conta?: number | null,
    dataInicio?: string | null,
    dataFim?: string | null,
    tipoDeTransacao?: string[] | null,
    tipoDespesa?: string | null,
    tipoReceita?: string | null,
    pessoaId?: number | null
  ): Observable<TotalBaixasMoedaDTO> {
    const params: any = {};
    if (conta != null) params.conta = conta;
    if (dataInicio) params.dataInicio = dataInicio;
    if (dataFim) params.dataFim = dataFim;
    if (tipoDeTransacao && tipoDeTransacao.length > 0) {
      params.tipoDeTransacao = tipoDeTransacao.join(',');
    }
    if (tipoDespesa) params.tipoDespesa = tipoDespesa;
    if (tipoReceita) params.tipoReceita = tipoReceita;
    if (pessoaId != null) params.pessoaId = pessoaId;

    return this.apiService.readWithParams<TotalBaixasMoedaDTO>('/baixa/total-baixas', params);
  }

  /**
   * Gera o PDF (Base64) do Extrato de Caixa da conta/moeda selecionada,
   * consumindo GET /api/relatorios/relatorio-extrato-de-caixa.
   *
   * A resposta e text/plain (string Base64), por isso usamos readText +
   * buildQueryParams (e nao readWithParams, que trataria a resposta como JSON).
   * O AuthInterceptor injeta o token automaticamente.
   */
  exportarExtratoDeCaixaPdf(params: ExtratoDeCaixaPdfParams): Observable<string> {
    const queryParams = this.apiService.buildQueryParams(params as any);
    const path = queryParams
      ? `/relatorios/relatorio-extrato-de-caixa?${queryParams}`
      : '/relatorios/relatorio-extrato-de-caixa';
    return this.apiService.readText<string>(path);
  }

}
